// src/pages/PlatformOrderTrackingPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  collection,
  getDocs,
  limit,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import { db } from "../firebaseConfig";

/* -------------------- utils -------------------- */
const toDate = (v) =>
  v instanceof Date ? v : v instanceof Timestamp ? v.toDate() : v ? new Date(v) : null;

const shortId = (id = "") => `#${id.slice(-5).toUpperCase()}`;

// dd.MM[.YYYY], HH:mm  (PL short)
const fmtShort = (d) => {
  if (!d) return "—";
  const now = new Date();
  const time = new Intl.DateTimeFormat("pl-PL", { hour: "2-digit", minute: "2-digit" }).format(d);
  const dayMonth = new Intl.DateTimeFormat("pl-PL", { day: "2-digit", month: "2-digit" }).format(d);
  return now.getFullYear() === d.getFullYear()
    ? `${dayMonth}, ${time}`
    : `${dayMonth}.${d.getFullYear()}, ${time}`;
};

// 3 steps only: 0 accepted, 1 inKitchen, 2 onDelivery (delivered marks step 2 as done)
const getStepIndex3 = ({ map = {}, status = {} }) => {
  if (map.onDelivery || status.onDelivery || map.orderDelivered) return 2;
  if (map.inKitchen || status.inKitchen) return 1;
  if (map.orderAccepted) return 0;
  return -1;
};

const tsFrom = ({ key, map, activity, fallbacks = [] }) => {
  if (map?.[key]) return toDate(map[key]);
  const types = [key, ...fallbacks];
  const hit = (activity || []).find((a) => types.includes(a?.type));
  return toDate(hit?.at);
};

// responsive
const useIsMobile = (bp = 768) => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < bp : true
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < bp);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [bp]);
  return isMobile;
};

/* -------------------- small UI atoms -------------------- */
const Pill = ({ children }) => (
  <div
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      border: "1px solid #ececec",
      borderRadius: 999,
      padding: "10px 14px",
      background: "#f7f7f7",
      fontSize: 12,
      lineHeight: 1,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </div>
);

const StepDot = ({ state }) => {
  const bg =
    state === "done" ? "var(--color-primary, #00a082)" :
    state === "active" ? "#1e88e5" : "#e0e0e0";
  const label = state === "done" ? "✓" : state === "active" ? "•" : "";
  return (
    <div
      style={{
        width: 26, height: 26, borderRadius: "50%",
        background: bg, color: "#fff",
        display: "grid", placeItems: "center", fontWeight: 700, flex: "0 0 26px",
      }}
    >
      {label}
    </div>
  );
};

const StepCard = ({ state, label, ts }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "12px 14px",
      border: "1px solid #EFEFEF",
      borderRadius: 12,
      background: "#fff",
      minHeight: 64,
      boxShadow: state === "active" ? "0 1px 4px rgba(0,0,0,0.06)" : "none",
    }}
  >
    <StepDot state={state} />
    <div>
      <div style={{ fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 12, color: "#777" }}>{ts ? fmtShort(ts) : "—"}</div>
    </div>
  </div>
);

const Arrow = () => (
  <div
    aria-hidden
    style={{
      alignSelf: "center",
      padding: "0 8px",
      color: "#9aa0a6",
      fontSize: 18,
      userSelect: "none",
    }}
  >
    ➜
  </div>
);

/* -------------------- page -------------------- */
export default function PlatformOrderTrackingPage() {
  const { orderId } = useParams();
  const isMobile = useIsMobile(768);

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  // last 5 hours
  const cutoff = useMemo(() => new Date(Date.now() - 5 * 60 * 60 * 1000), []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError("");
        setOrder(null);

        if (!orderId) { setError("Brak identyfikatora zamówienia."); return; }

        const q = query(
          collection(db, "orders"),
          where("orderId", "==", orderId),
          limit(1)
        );
        const snap = await getDocs(q);
        if (snap.empty) { setError("Nie znaleziono zamówienia."); return; }

        const d = snap.docs[0].data() || {};
        const createdAt = toDate(d.createdAt);
        if (!createdAt) { setError("Zamówienie nie ma poprawnej daty."); return; }
        if (createdAt < cutoff) { setError("Zamówienie jest starsze niż ostatnie 5 godzin."); return; }

        if (alive) setOrder({ id: snap.docs[0].id, ...d, createdAt });
      } catch (e) {
        console.error(e);
        setError("Wystąpił błąd podczas wyszukiwania zamówienia.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [orderId, cutoff]);

  // Centered loading
  if (loading)
    return (
      <div style={outerWrapStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: "center", padding: 40 }}>Ładowanie…</div>
        </div>
      </div>
    );

  // Centered error
  if (error)
    return (
      <div style={outerWrapStyle}>
        <div style={cardStyle}>
          <h2 style={{ margin: "0 0 12px" }}>Śledzenie zamówienia</h2>
          <div style={{ color: "#b00" }}>{error}</div>
        </div>
      </div>
    );

  const { createdAt, orderStatusMap = {}, orderStatus = {}, activity = [] } = order || {};

  // 3-step state
  const stepIndex = getStepIndex3({ map: orderStatusMap, status: orderStatus });
  const delivered = !!orderStatusMap.orderDelivered; // marks last step as done

  // timestamps
  const tsAccepted = tsFrom({ key: "orderAccepted", map: orderStatusMap, activity });
  const tsKitchen  = tsFrom({ key: "inKitchen",     map: orderStatusMap, activity });
  const tsDelivery =
    tsFrom({ key: "onDelivery", map: orderStatusMap, activity, fallbacks: ["delivery"] }) ||
    (orderStatusMap?.orderDelivered ? toDate(orderStatusMap.orderDelivered) : null);

  // steps (3 only)
  const steps = [
    { key: "orderAccepted", label: "Potwierdzone", ts: tsAccepted },
    { key: "inKitchen",     label: "W kuchni",     ts: tsKitchen  },
    { key: "onDelivery",    label: "W dostawie",   ts: tsDelivery },
  ];

  // state per index (done/active/todo). If delivered, last is done.
  const stateFor = (i) => {
    if (delivered) return "done";
    if (i < stepIndex) return "done";
    if (i === stepIndex) return "active";
    return "todo";
  };

  const bigStatus =
    delivered ? "Zamówienie dostarczone" :
    stepIndex === 2 ? "Twoje zamówienie jest w drodze" :
    stepIndex === 1 ? "Twoje zamówienie jest przygotowywane" :
    stepIndex === 0 ? "Zamówienie przyjęte" :
    "Oczekiwanie na potwierdzenie";

  return (
    <div style={outerWrapStyle}>
      <div style={cardStyle}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
            justifyContent: isMobile ? "flex-start" : "space-between",
            flexWrap: "wrap",
            width: "100%",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <div style={{ fontSize: 22, fontWeight: 800, color: "#111" }}>{bigStatus}</div>
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "flex-start" : "center",
            }}
          >
            <Pill><span style={{ opacity: 0.7 }}>Order ID:</span> <strong>{shortId(orderId)}</strong></Pill>
            <Pill><span style={{ opacity: 0.7 }}>Utworzone:</span> <strong>{fmtShort(createdAt)}</strong></Pill>
          </div>
        </div>

        {/* Steps: arrows on desktop, stacked on mobile */}
        {isMobile ? (
          <div style={{ display: "grid", gap: 12, marginTop: 16 }}>
            {steps.map((s, i) => (
              <StepCard key={s.key} state={stateFor(i)} label={s.label} ts={s.ts} />
            ))}
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "stretch",
              gap: 0,
              marginTop: 16,
              flexWrap: "nowrap",
              overflowX: "auto",
            }}
          >
            {steps.map((s, i) => (
              <React.Fragment key={s.key}>
                <div style={{ minWidth: 220, flex: "0 0 220px" }}>
                  <StepCard state={stateFor(i)} label={s.label} ts={s.ts} />
                </div>
                {i < steps.length - 1 && <Arrow />}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------- layout styles -------------------- */
const outerWrapStyle = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#fafafa",
  padding: "16px",
};

const cardStyle = {
  maxWidth: 960,
  width: "100%",
  padding: 16,
  border: "1px solid #e9e9e9",
  borderRadius: 16,
  background: "#fff",
};
